import type { PageHelp } from 'help/types';

export const remindersHelp: PageHelp = {
  title: 'Reminders',
  intro:
    'Dunning notices (reminders) for unpaid premium instalments from the clients’ payment plans. Here you find existing reminders, create a new one from a client’s open instalments (or one addressed to a payer, covering the instalments of several clients), download its PDF documents and send it to the client by email; payments that later arrive through bank statements show up in the paid amounts and status icons.',
  sections: [
    'Getting around',
    'Finding a reminder',
    'Reading the list',
    'Documents and row actions',
    'Create a reminder',
    'Send a reminder by email',
    'Delete a reminder',
  ],
  contexts: {
    'create-dialog': 'Select a client in the "Client" field (or a payer in the "Payer" field), then click "Create reminder" next to it.',
    'send-dialog': 'Click the envelope icon ("Send by email") in a reminder row; it is shown only when the reminder has a document.',
    'delete-dialog': 'Click the delete icon ("Delete reminder") in a reminder row.',
  },
  tips: [
    'Reminders are built from unpaid instalments of the payment plans. The money itself is matched on the Bank statements page; once an instalment is paid, the paid amounts and status icons of its reminder change here.',
    'Status icons: a green tick means paid, a half circle partially paid, an empty circle not paid, and a question mark means the premium is not collected by the broker. The upper icon is for premium, the lower one for the discount.',
    'A created reminder cannot be edited on this page — its documents are generated once, at creation. To correct one, delete it and create a new reminder.',
    'Outside the production environment every generated PDF carries a "THIS IS A TEST DOCUMENT" watermark.',
    'Users with the limited PCT role can view reminders, but the server refuses creating, sending and deleting them.',
  ],
  steps: [
    {
      selector: '[data-help="reminders-page-switcher"]',
      title: 'Switch to another PCT page',
      body: 'The icons in the first toolbar row open the other PCT pages — bank statements, insurer reports, client balance, payment plans and so on. The highlighted icon is the page you are on. Users with limited access (neither admin nor GOS user) only get the Payment plans and Reminders pages; here they can search, filter, send reminders by email, download documents and export to Excel, but cannot create or delete reminders.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pereiti į kitą PCT puslapį',
          body: 'Pirmosios įrankių juostos eilutės piktogramos atveria kitus PCT puslapius — banko išrašus, draudikų ataskaitas, kliento balansą, mokėjimų planus ir kt. Paryškinta piktograma rodo, kuriame puslapyje esate. Ribotos prieigos naudotojams (ne administratoriams ir ne GOS naudotojams) prieinami tik „Mokėjimų planas“ ir „Priminimai“ puslapiai; čia jie gali ieškoti, filtruoti, siųsti priminimus el. paštu, atsisiųsti dokumentus ir eksportuoti į Excel, bet negali kurti ar trinti priminimų.',
        },
        lv: {
          title: 'Pāriet uz citu PCT lapu',
          body: 'Rīkjoslas pirmās rindas ikonas atver citas PCT lapas — bankas izrakstus, apdrošinātāju atskaites, klienta bilanci, maksājumu plānus u. c. Izceltā ikona rāda lapu, kurā atrodaties. Lietotājiem ar ierobežotu piekļuvi (ne administratoriem un ne GOS lietotājiem) pieejamas tikai “Payment plans” un “Reminders” lapas; šeit viņi var meklēt, filtrēt, sūtīt atgādinājumus pa e-pastu, lejupielādēt dokumentus un eksportēt uz Excel, bet nevar izveidot vai dzēst atgādinājumus.',
        },
      },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: 'Download the user manual',
      body: 'Downloads the complete PCT user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Lithuanian or Latvian when the app is in that language, English otherwise. The page guide offers the same download (the book next to EN / LT / LV) in the guide’s language.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atsisiųsti naudotojo vadovą',
          body: 'Atsisiunčia visą PCT naudotojo vadovą PDF formatu su visų puslapių ekrano kopijomis. Gaunate jį kalba, pasirinkta „Nustatymuose“: lietuvių arba latvių, kai programa veikia ta kalba, kitu atveju – anglų. Puslapio vadove tas pats atsisiuntimas (knygos piktograma šalia EN / LT / LV) pateikiamas vadovo kalba.',
        },
        lv: {
          title: 'Lejupielādēt lietotāja rokasgrāmatu',
          body: 'Lejupielādē pilnu PCT lietotāja rokasgrāmatu PDF formātā ar visu lapu ekrānuzņēmumiem. Jūs to saņemat valodā, kas izvēlēta sadaļā “Settings”: latviešu vai lietuviešu, ja lietotne darbojas šajā valodā, citādi – angļu. Lapas ceļvedī tā pati lejupielāde (grāmatas ikona blakus EN / LT / LV) ir pieejama ceļveža valodā.',
        },
      },
    },
    {
      selector: '[data-help="reminders-companies"]',
      title: 'Choose the GrECo companies',
      body: 'Tick the GrECo companies whose reminders you want to see; the list reloads when the menu closes. At least one company must stay selected. With several companies ticked, "Create reminder" asks which company the new reminder is for.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pasirinkti GrECo įmones',
          body: 'Pažymėkite GrECo įmones, kurių priminimus norite matyti; sąrašas persikrauna uždarius meniu. Bent viena įmonė turi likti pažymėta. Pažymėjus kelias įmones, „Create reminder“ paklaus, kuriai įmonei kuriamas priminimas.',
        },
        lv: {
          title: 'Izvēlēties GrECo uzņēmumus',
          body: 'Atzīmējiet GrECo uzņēmumus, kuru atgādinājumus vēlaties redzēt; saraksts pārlādējas, kad izvēlne tiek aizvērta. Vismaz vienam uzņēmumam jāpaliek atzīmētam. Ja atzīmēti vairāki uzņēmumi, „Create reminder“ pajautās, kuram uzņēmumam atgādinājums tiek veidots.',
        },
      },
    },
    {
      selector: '[data-help="reminders-refresh"]',
      title: 'Reload the reminders',
      body: 'Loads the reminders again from the server with the current filters. Use it after a colleague has created, sent or deleted a reminder, or after payments have been cleared, to see the latest paid amounts.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atnaujinti priminimų sąrašą',
          body: 'Iš naujo įkelia priminimus iš serverio pagal esamus filtrus. Naudokite, kai kolega sukūrė, išsiuntė ar ištrynė priminimą arba buvo sudengti mokėjimai, kad matytumėte naujausias apmokėtas sumas.',
        },
        lv: {
          title: 'Pārlādēt atgādinājumus',
          body: 'No jauna ielādē atgādinājumus no servera ar pašreizējiem filtriem. Izmantojiet, ja kolēģis ir izveidojis, nosūtījis vai dzēsis atgādinājumu vai ja maksājumi ir sasaistīti, lai redzētu jaunākās apmaksātās summas.',
        },
      },
    },
    {
      selector: '[data-help="reminders-client"]',
      title: 'Narrow the list to one client',
      body: 'Type part of a client name or number and pick the client; the list then shows only that client’s reminders. The list is filtered by a client or by a payer, never both — picking a client clears the "Payer" field. A client (or a payer) must be selected before you can create a reminder — the "Create reminder" button appears only then. Clear the field to see all clients again.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Rodyti vieno kliento priminimus',
          body: 'Įveskite kliento pavadinimo ar numerio dalį ir pasirinkite klientą („Pasirinkti klientą“); sąraše liks tik jo priminimai. Sąrašas filtruojamas pagal klientą arba pagal mokėtoją, bet ne pagal abu — pasirinkus klientą išvalomas laukas „Mokėtojas“. Klientą (arba mokėtoją) būtina pasirinkti prieš kuriant priminimą — mygtukas „Create reminder“ atsiranda tik tada. Išvalę lauką vėl matysite visus klientus.',
        },
        lv: {
          title: 'Rādīt viena klienta atgādinājumus',
          body: 'Ierakstiet daļu no klienta nosaukuma vai numura un izvēlieties klientu („Select client“); sarakstā paliks tikai šī klienta atgādinājumi. Sarakstu filtrē pēc klienta vai pēc maksātāja, nekad pēc abiem — izvēloties klientu, lauks „Payer“ tiek notīrīts. Klients (vai maksātājs) ir jāizvēlas, pirms var izveidot atgādinājumu — poga „Create reminder“ parādās tikai tad. Notīriet lauku, lai atkal redzētu visus klientus.',
        },
      },
    },
    {
      selector: '[data-help="reminders-payer"]',
      title: 'Narrow the list to one payer',
      body: 'Pick a payer instead of a client to see every reminder addressed to that payer, whichever clients it pays for. Picking a payer clears the "Client" field. With a payer selected, "Create reminder" creates one reminder addressed to that payer, covering the instalments of the clients you pick in the dialog.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Rodyti vieno mokėtojo priminimus',
          body: 'Vietoj kliento pasirinkite mokėtoją („Search payers“), kad matytumėte visus šiam mokėtojui adresuotus priminimus, nesvarbu, už kuriuos klientus jis moka. Pasirinkus mokėtoją išvalomas laukas „Klientas“. Kai pasirinktas mokėtojas, „Create reminder“ sukuria vieną šiam mokėtojui adresuotą priminimą iš lange pasirinktų klientų įmokų.',
        },
        lv: {
          title: 'Rādīt viena maksātāja atgādinājumus',
          body: 'Klienta vietā izvēlieties maksātāju („Search payers“), lai redzētu visus šim maksātājam adresētos atgādinājumus neatkarīgi no tā, par kuriem klientiem tas maksā. Izvēloties maksātāju, lauks „Client“ tiek notīrīts. Ja izvēlēts maksātājs, „Create reminder“ izveido vienu šim maksātājam adresētu atgādinājumu no logā izvēlēto klientu iemaksām.',
        },
      },
    },
    {
      selector: '[data-help="reminders-search"]',
      title: 'Search the loaded reminders',
      body: 'Filters the reminders already in the list by reminder number, client name, insurer, comment or a date (created, last payment, due date) typed as DD.MM.YYYY. It searches only what the date range and the other filters have loaded.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Ieškoti įkeltų priminimų',
          body: 'Filtruoja jau sąraše esančius priminimus pagal priminimo numerį, kliento pavadinimą, draudiką, komentarą arba datą (sukūrimo, paskutinio mokėjimo, „Sumokėti iki“), įvestą formatu DD.MM.YYYY. Ieškoma tik tarp priminimų, įkeltų pagal datų intervalą ir kitus filtrus.',
        },
        lv: {
          title: 'Meklēt ielādētajos atgādinājumos',
          body: 'Filtrē sarakstā jau esošos atgādinājumus pēc atgādinājuma numura, klienta nosaukuma, apdrošinātāja, komentāra vai datuma (izveides, pēdējā maksājuma, „Due date“), ierakstīta formātā DD.MM.YYYY. Meklē tikai starp atgādinājumiem, kas ielādēti pēc datumu intervāla un citiem filtriem.',
        },
      },
    },
    {
      selector: '[data-help="reminders-from-date"]',
      title: 'Set the start of the creation-date range',
      body: 'Only reminders created on or after this date are loaded. By default the range starts about two months back; move it earlier to find older reminders. You can type the date as D.M.YYYY.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Nustatyti sukūrimo datų intervalo pradžią',
          body: 'Įkeliami tik priminimai, sukurti šią dieną arba vėliau. Pagal nutylėjimą intervalas prasideda maždaug prieš du mėnesius; norėdami rasti senesnius priminimus, nustatykite ankstesnę datą. Datą galite įvesti formatu D.M.YYYY.',
        },
        lv: {
          title: 'Iestatīt izveides datumu intervāla sākumu',
          body: 'Tiek ielādēti tikai atgādinājumi, kas izveidoti šajā datumā vai vēlāk. Pēc noklusējuma intervāls sākas aptuveni pirms diviem mēnešiem; lai atrastu vecākus atgādinājumus, iestatiet agrāku datumu. Datumu var ierakstīt formātā D.M.YYYY.',
        },
      },
    },
    {
      selector: '[data-help="reminders-to-date"]',
      title: 'Set the end of the creation-date range',
      body: 'Only reminders created up to and including this date are loaded; by default it is tomorrow, so today’s new reminders are included. The list reloads as soon as the date changes.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Nustatyti sukūrimo datų intervalo pabaigą',
          body: 'Įkeliami tik priminimai, sukurti iki šios dienos imtinai; pagal nutylėjimą tai rytojus, todėl šiandien sukurti priminimai įtraukiami. Pakeitus datą sąrašas persikrauna iš karto.',
        },
        lv: {
          title: 'Iestatīt izveides datumu intervāla beigas',
          body: 'Tiek ielādēti tikai atgādinājumi, kas izveidoti līdz šim datumam ieskaitot; pēc noklusējuma tā ir rītdiena, tāpēc šodien izveidotie atgādinājumi ir iekļauti. Mainot datumu, saraksts uzreiz pārlādējas.',
        },
      },
    },
    {
      selector: '[data-help="reminders-policy-number"]',
      title: 'Find reminders for a policy number',
      body: 'Type a full policy number to load only reminders that contain an instalment of that policy. The number must match exactly; a partial number returns no reminders.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Rasti priminimus pagal poliso numerį',
          body: 'Įveskite visą poliso numerį („Poliso numeris“) — bus įkelti tik priminimai, kuriuose yra šio poliso įmoka. Numeris turi sutapti tiksliai; įvedus tik dalį numerio priminimų nerasite.',
        },
        lv: {
          title: 'Atrast atgādinājumus pēc polises numura',
          body: 'Ierakstiet pilnu polises numuru („Policy Number“), lai ielādētu tikai atgādinājumus, kuros ir šīs polises iemaksa. Numuram jāsakrīt precīzi; ievadot tikai daļu numura, atgādinājumi netiks atrasti.',
        },
      },
    },
    {
      selector: '[data-help="reminders-policy-subnumber"]',
      title: 'Find reminders for a policy sub-number',
      body: 'Works like the policy number filter, but for the policy sub-number. It also needs the exact value and can be combined with the policy number and the client.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Rasti priminimus pagal poliso sub numerį',
          body: 'Veikia kaip poliso numerio filtras, tik pagal poliso sub numerį („Poliso Sub numeris“). Taip pat reikia tikslios reikšmės; filtrą galima derinti su poliso numeriu ir klientu.',
        },
        lv: {
          title: 'Atrast atgādinājumus pēc polises apakšnumura',
          body: 'Darbojas kā polises numura filtrs, tikai pēc polises apakšnumura („Policy Sub Number“). Arī šeit vajadzīga precīza vērtība; filtru var apvienot ar polises numuru un klientu.',
        },
      },
    },
    {
      selector: '[data-help="reminders-results-count"]',
      title: 'See how many reminders match',
      body: 'Shows the number of reminders currently visible in the list after all filters and the search box. If it shows 0, widen the date range or clear the search.',
      section: 'Finding a reminder',
      i18n: {
        lt: {
          title: 'Matyti, kiek priminimų atitinka',
          body: 'Rodo, kiek priminimų šiuo metu matoma sąraše pritaikius visus filtrus ir paiešką. Jei rodoma 0, išplėskite datų intervalą arba išvalykite paiešką.',
        },
        lv: {
          title: 'Redzēt, cik atgādinājumu atbilst',
          body: 'Rāda, cik atgādinājumu pašlaik redzami sarakstā pēc visiem filtriem un meklēšanas. Ja rāda 0, paplašiniet datumu intervālu vai notīriet meklēšanu.',
        },
      },
    },
    {
      selector: '[data-help="reminders-grid"]',
      title: 'Read the reminders list',
      body: 'One row per reminder, newest first: reminder number, client, payer, insurer, created and due dates, currency, and three "paid / amount" pairs — booked premium, total premium and discount. A reminder created by payer is shown on a green-tinted row, and its client column lists every client whose instalments it contains. "BPC" is ticked when the premium is collected by the broker; the "Comment" column holds the comment entered at creation.',
      section: 'Reading the list',
      i18n: {
        lt: {
          title: 'Skaityti priminimų sąrašą',
          body: 'Kiekvienas priminimas — atskira eilutė, naujausi viršuje: priminimo numeris, klientas, mokėtojas, draudikas, sukūrimo data ir „Sumokėti iki“, valiuta bei trys „apmokėta / suma“ poros — užregistruota įmoka, visa įmoka („Iš viso apmokėta / suma“) ir nuolaida. Mokėtojui sukurtas priminimas rodomas žalsvame fone, o jo kliento stulpelyje išvardijami visi klientai, kurių įmokas jis apima. „ĮRM“ pažymėta, kai įmokas renka brokeris; stulpelyje „Komentaras“ — kuriant įvestas komentaras.',
        },
        lv: {
          title: 'Lasīt atgādinājumu sarakstu',
          body: 'Katrs atgādinājums ir atsevišķa rinda, jaunākie augšā: atgādinājuma numurs, klients, maksātājs, apdrošinātājs, izveides datums un „Due date“, valūta un trīs „apmaksāts / summa“ pāri — reģistrētā prēmija, kopējā prēmija un atlaide. Maksātājam izveidots atgādinājums tiek rādīts zaļganā fonā, un tā klienta kolonnā uzskaitīti visi klienti, kuru iemaksas tajā iekļautas. „BPC“ ir atzīmēts, ja prēmiju iekasē brokeris; kolonnā „Comment“ ir izveidošanas brīdī ievadītais komentārs.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-select"]',
      title: 'Open a reminder’s instalments',
      body: 'Tick the round checkbox at the start of a row to load that reminder’s instalments into the lower list. Only one reminder can be selected at a time; ticking another one replaces the lower list.',
      section: 'Reading the list',
      optional: true,
      i18n: {
        lt: {
          title: 'Atverti priminimo įmokas',
          body: 'Pažymėkite apvalų žymimąjį langelį eilutės pradžioje — šio priminimo įmokos bus įkeltos į apatinį sąrašą. Vienu metu galima pasirinkti tik vieną priminimą; pažymėjus kitą, apatinis sąrašas pakeičiamas.',
        },
        lv: {
          title: 'Atvērt atgādinājuma iemaksas',
          body: 'Atzīmējiet apaļo izvēles rūtiņu rindas sākumā, lai šī atgādinājuma iemaksas ielādētu apakšējā sarakstā. Vienlaikus var izvēlēties tikai vienu atgādinājumu; atzīmējot citu, apakšējais saraksts tiek aizstāts.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-status"]',
      title: 'Check whether the reminder is paid',
      body: 'The upper icon shows the premium: green tick paid, half circle partially paid, empty circle not paid, question mark premium not collected by the broker. The lower icon shows the discount the same way, with a grey tick when there is no discount. Hover an icon for its meaning.',
      section: 'Reading the list',
      optional: true,
      i18n: {
        lt: {
          title: 'Patikrinti, ar priminimas apmokėtas',
          body: 'Viršutinė piktograma rodo įmoką: žalia varnelė — sumokėta, pusė apskritimo — sumokėta iš dalies, tuščias apskritimas — nesumokėta, klaustukas — įmokos nerenka brokeris. Apatinė piktograma taip pat rodo nuolaidą; pilka varnelė reiškia, kad nuolaidos nėra. Užveskite pelę ant piktogramos, kad pamatytumėte paaiškinimą.',
        },
        lv: {
          title: 'Pārbaudīt, vai atgādinājums ir apmaksāts',
          body: 'Augšējā ikona rāda prēmiju: zaļa ķeksīte — apmaksāta, pusaplis — daļēji apmaksāta, tukšs aplis — nav apmaksāta, jautājuma zīme — prēmiju neiekasē brokeris. Apakšējā ikona tāpat rāda atlaidi; pelēka ķeksīte nozīmē, ka atlaides nav. Novietojiet peli uz ikonas, lai redzētu skaidrojumu.',
        },
      },
    },
    {
      selector: '[data-help="reminders-entries-grid"]',
      title: 'Review the instalments of the selected reminder',
      body: 'Lists the payment-plan instalments included in the selected reminder: policy, insurer, policy number and sub-number, insurance line, booking date, premium period and the paid / amount pairs per instalment. For a reminder created by payer it also shows the client of each instalment. It stays empty until you tick a reminder in the upper list.',
      section: 'Reading the list',
      i18n: {
        lt: {
          title: 'Peržiūrėti pasirinkto priminimo įmokas',
          body: 'Rodo į pasirinktą priminimą įtrauktas mokėjimų plano įmokas: polisą, draudiką, poliso numerį ir sub numerį, draudimo rūšį, įmokos datą, įmokos laikotarpį ir kiekvienos įmokos „apmokėta / suma“ poras. Mokėtojui sukurto priminimo sąraše rodomas ir kiekvienos įmokos klientas. Sąrašas tuščias, kol viršutiniame sąraše nepažymite priminimo.',
        },
        lv: {
          title: 'Pārskatīt izvēlētā atgādinājuma iemaksas',
          body: 'Rāda izvēlētajā atgādinājumā iekļautās maksājumu plāna iemaksas: polisi, apdrošinātāju, polises numuru un apakšnumuru, apdrošināšanas veidu, iemaksas datumu, prēmijas periodu un katras iemaksas „apmaksāts / summa“ pārus. Maksātājam izveidotam atgādinājumam tiek rādīts arī katras iemaksas klients. Saraksts ir tukšs, kamēr augšējā sarakstā nav atzīmēts atgādinājums.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-download"]',
      title: 'Download the reminder document',
      body: 'Opens the main PDF generated when the reminder was created — the version that includes discounts. In the email dialog it is listed as "Document with discount".',
      section: 'Documents and row actions',
      optional: true,
      i18n: {
        lt: {
          title: 'Atsisiųsti priminimo dokumentą',
          body: 'Atveria pagrindinį PDF, sugeneruotą kuriant priminimą („Atsisiųsti dokumentą“) — versiją su nuolaidomis. El. laiško lange jis vadinamas „Document with discount“.',
        },
        lv: {
          title: 'Lejupielādēt atgādinājuma dokumentu',
          body: 'Atver galveno PDF, kas ģenerēts, veidojot atgādinājumu („Download document“) — versiju ar atlaidēm. E-pasta logā tas saucas „Document with discount“.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-download-additional"]',
      title: 'Download the additional document',
      body: 'Opens the second PDF of the reminder — the version without discounts, listed as "Document without discount" in the email dialog. It is shown only when the reminder has this second document.',
      section: 'Documents and row actions',
      optional: true,
      i18n: {
        lt: {
          title: 'Atsisiųsti papildomą dokumentą',
          body: 'Atveria antrąjį priminimo PDF — versiją be nuolaidų; el. laiško lange ji vadinama „Document without discount“. Tai antrasis atsisiuntimo mygtukas eilutėje, rodomas tik tada, kai priminimas turi šį dokumentą.',
        },
        lv: {
          title: 'Lejupielādēt papildu dokumentu',
          body: 'Atver atgādinājuma otro PDF („Download additional document“) — versiju bez atlaidēm, e-pasta logā saukta „Document without discount“. Poga redzama tikai tad, ja atgādinājumam ir šis otrais dokuments.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-export-excel"]',
      title: 'Export the instalments to Excel',
      body: 'Appears on the selected row only. Downloads the instalments shown in the lower list as an .xlsx file named "Reminder Entries" with the current date and time; for a reminder created by payer it includes the client of each instalment.',
      section: 'Documents and row actions',
      optional: true,
      i18n: {
        lt: {
          title: 'Eksportuoti įmokas į Excel',
          body: 'Rodoma tik pažymėtoje eilutėje („Export Excel“). Atsisiunčia apatiniame sąraše rodomas įmokas kaip .xlsx failą „Reminder Entries“ su dabartine data ir laiku; mokėtojui sukurto priminimo faile yra ir kiekvienos įmokos klientas.',
        },
        lv: {
          title: 'Eksportēt iemaksas uz Excel',
          body: 'Redzama tikai atzīmētajā rindā („Export Excel“). Lejupielādē apakšējā sarakstā redzamās iemaksas kā .xlsx failu „Reminder Entries“ ar pašreizējo datumu un laiku; maksātājam izveidota atgādinājuma failā ir arī katras iemaksas klients.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-send-email"]',
      title: 'Send the reminder by email',
      body: 'Opens the "Send by email" dialog for this reminder with the recipient, subject, text and documents already filled in. The envelope is shown only for reminders that have a generated document.',
      section: 'Documents and row actions',
      optional: true,
      i18n: {
        lt: {
          title: 'Siųsti priminimą el. paštu',
          body: 'Atveria šio priminimo langą „Send by email“, kuriame jau užpildyti gavėjas, tema, tekstas ir dokumentai. Voko piktograma rodoma tik priminimams, kurie turi sugeneruotą dokumentą.',
        },
        lv: {
          title: 'Nosūtīt atgādinājumu pa e-pastu',
          body: 'Atver šī atgādinājuma logu „Send by email“, kurā jau aizpildīts saņēmējs, temats, teksts un dokumenti. Aploksnes ikona redzama tikai atgādinājumiem, kuriem ir ģenerēts dokuments.',
        },
      },
    },
    {
      selector: '[data-help="reminders-row-delete"]',
      title: 'Delete a reminder',
      body: 'Opens a confirmation dialog for deleting this reminder. Use it for a reminder created by mistake or with wrong contents — reminders cannot be edited after creation. Not shown to users with limited access (neither admin nor GOS user).',
      section: 'Documents and row actions',
      optional: true,
      i18n: {
        lt: {
          title: 'Ištrinti priminimą',
          body: 'Atveria šio priminimo trynimo patvirtinimo langą („Delete reminder“). Naudokite, jei priminimas sukurtas per klaidą arba su netinkamu turiniu — sukurtų priminimų redaguoti negalima. Ribotos prieigos naudotojams (ne administratoriams ir ne GOS naudotojams) nerodoma.',
        },
        lv: {
          title: 'Dzēst atgādinājumu',
          body: 'Atver šī atgādinājuma dzēšanas apstiprinājuma logu („Delete reminder“). Izmantojiet, ja atgādinājums izveidots kļūdas pēc vai ar nepareizu saturu — izveidotus atgādinājumus rediģēt nevar. Lietotājiem ar ierobežotu piekļuvi (ne administratoriem un ne GOS lietotājiem) netiek rādīta.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-button"]',
      title: 'Start a new reminder',
      body: 'Opens the "Create reminder" dialog for the selected client — or, when the list is filtered by payer, for that payer. It appears only after a client or a payer is chosen; with several GrECo companies ticked it first asks for the company, because a reminder always belongs to one company. Nothing is saved until you confirm in the dialog. Not shown to users with limited access (neither admin nor GOS user).',
      section: 'Create a reminder',
      i18n: {
        lt: {
          title: 'Pradėti naują priminimą',
          body: 'Atveria pasirinkto kliento — arba, kai sąrašas filtruojamas pagal mokėtoją, to mokėtojo — langą „Create reminder“. Mygtukas rodomas tik pasirinkus klientą ar mokėtoją; jei pažymėtos kelios GrECo įmonės, pirmiausia reikės pasirinkti įmonę, nes priminimas visada priklauso vienai įmonei. Kol nepatvirtinsite lange, niekas neišsaugoma. Ribotos prieigos naudotojams (ne administratoriams ir ne GOS naudotojams) mygtukas nerodomas.',
        },
        lv: {
          title: 'Sākt jaunu atgādinājumu',
          body: 'Atver izvēlētā klienta — vai, ja saraksts filtrēts pēc maksātāja, šī maksātāja — logu „Create reminder“. Poga redzama tikai pēc klienta vai maksātāja izvēles; ja atzīmēti vairāki GrECo uzņēmumi, vispirms jāizvēlas uzņēmums, jo atgādinājums vienmēr pieder vienam uzņēmumam. Kamēr neapstiprināt logā, nekas netiek saglabāts. Lietotājiem ar ierobežotu piekļuvi (ne administratoriem un ne GOS lietotājiem) poga netiek rādīta.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-dates"]',
      title: 'Choose the booking-date range of the instalments',
      body: 'Only unpaid instalments with a booking date inside this range are suggested. It starts as today to today + 15 days; move the start back to include overdue instalments. Instalments older than the system’s minimum query date are never offered.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti įmokų datų intervalą',
          body: 'Siūlomos tik nesumokėtos įmokos, kurių įmokos data patenka į šį intervalą. Pradžioje jis nuo šiandien iki šiandien + 15 dienų; norėdami įtraukti pradelstas įmokas, perkelkite pradžią atgal. Įmokos, senesnės už sistemos minimalią užklausų datą, niekada nesiūlomos.',
        },
        lv: {
          title: 'Izvēlēties iemaksu datumu intervālu',
          body: 'Tiek piedāvātas tikai neapmaksātas iemaksas, kuru iemaksas datums ir šajā intervālā. Sākumā tas ir no šodienas līdz šodienai + 15 dienas; lai iekļautu kavētās iemaksas, pārvietojiet sākumu atpakaļ. Iemaksas, kas vecākas par sistēmas minimālo vaicājuma datumu, netiek piedāvātas.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-insurers"]',
      title: 'Pick the insurer country and insurers',
      body: 'Choose the insurer country first (it starts as the company’s country), then one or more insurers from that country. An insurer is required when "Broker premium collection" is "No", and in that case the reminder may cover only one insurer — the server rejects several.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti draudiko šalį ir draudikus',
          body: 'Pirmiausia pasirinkite draudiko šalį (iš pradžių nustatoma įmonės šalis), tada vieną ar kelis tos šalies draudikus („Pasirinkti draudiką“). Draudikas privalomas, kai „Broker premium collection“ yra „No“, ir tuomet priminimas gali apimti tik vieną draudiką — serveris kelių nepriims.',
        },
        lv: {
          title: 'Izvēlēties apdrošinātāja valsti un apdrošinātājus',
          body: 'Vispirms izvēlieties apdrošinātāja valsti (sākumā tā ir uzņēmuma valsts), tad vienu vai vairākus šīs valsts apdrošinātājus („Search insurers“). Apdrošinātājs ir obligāts, ja „Broker premium collection“ ir „No“, un tad atgādinājums drīkst aptvert tikai vienu apdrošinātāju — serveris vairākus noraida.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-payers"]',
      title: 'Limit the instalments to certain payers',
      body: 'Optional. Search and pick payers to suggest only instalments paid by them. Leave it empty to include every payer of the client’s instalments. Shown only for a reminder by client.',
      section: 'Create a reminder',
      context: 'create-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Apriboti įmokas pagal mokėtojus',
          body: 'Neprivaloma. Suraskite ir pasirinkite mokėtojus („Search payers“), kad būtų siūlomos tik jų mokamos įmokos. Palikite tuščią, jei norite įtraukti visus kliento įmokų mokėtojus. Rodoma tik kuriant priminimą klientui.',
        },
        lv: {
          title: 'Ierobežot iemaksas pēc maksātājiem',
          body: 'Nav obligāti. Atrodiet un izvēlieties maksātājus („Search payers“), lai piedāvātu tikai viņu maksājamās iemaksas. Atstājiet tukšu, lai iekļautu visus klienta iemaksu maksātājus. Redzams tikai, veidojot atgādinājumu klientam.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-clients"]',
      title: 'Choose the clients of a payer’s reminder',
      body: 'Shown instead of the payers field when the reminder is created by payer. Pick at least one client: the instalments of the selected clients are suggested, whoever is recorded as their policy payer, and one reminder addressed to the payer covers them all. "Search" stays disabled until a client is picked.',
      section: 'Create a reminder',
      context: 'create-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Pasirinkti mokėtojo priminimo klientus',
          body: 'Rodoma vietoj mokėtojų lauko, kai priminimas kuriamas mokėtojui. Pasirinkite bent vieną klientą („Pasirinkti klientus“): siūlomos pasirinktų klientų įmokos, nesvarbu, kas nurodytas jų poliso mokėtoju, o vienas mokėtojui adresuotas priminimas apima jas visas. „Ieškoti“ neaktyvus, kol nepasirinktas klientas.',
        },
        lv: {
          title: 'Izvēlēties maksātāja atgādinājuma klientus',
          body: 'Redzams maksātāju lauka vietā, ja atgādinājumu veido maksātājam. Izvēlieties vismaz vienu klientu („Select clients“): tiek piedāvātas izvēlēto klientu iemaksas neatkarīgi no tā, kas norādīts kā to polises maksātājs, un viens maksātājam adresēts atgādinājums aptver tās visas. „Search“ nav aktīva, kamēr nav izvēlēts klients.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-currency"]',
      title: 'Choose the currency',
      body: 'A reminder is always in one currency; only instalments in the selected currency are suggested. It starts as EUR.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti valiutą',
          body: 'Priminimas visada sudaromas viena valiuta; siūlomos tik pasirinktos valiutos įmokos („Select currency code“). Iš pradžių nustatyta EUR.',
        },
        lv: {
          title: 'Izvēlēties valūtu',
          body: 'Atgādinājums vienmēr ir vienā valūtā; tiek piedāvātas tikai izvēlētās valūtas iemaksas („Select currency code“). Sākumā iestatīts EUR.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-service-segments"]',
      title: 'Limit by service segment',
      body: 'Optional. Pick service segments to suggest only instalments whose insurance line belongs to them. Leave it empty for all lines of business.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Apriboti pagal paslaugų segmentą',
          body: 'Neprivaloma. Pasirinkite paslaugų segmentus („Select service segments“), kad būtų siūlomos tik įmokos, kurių draudimo rūšis priklauso šiems segmentams. Palikite tuščią, jei norite visų draudimo rūšių.',
        },
        lv: {
          title: 'Ierobežot pēc pakalpojumu segmenta',
          body: 'Nav obligāti. Izvēlieties pakalpojumu segmentus („Select service segments“), lai piedāvātu tikai iemaksas, kuru apdrošināšanas veids pieder šiem segmentiem. Atstājiet tukšu visiem apdrošināšanas veidiem.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-policies"]',
      title: 'Limit to specific policy numbers',
      body: 'Optional. Type a policy number and press Enter to add it; add several if needed. Only instalments of exactly these policy numbers are suggested.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Apriboti iki konkrečių polisų numerių',
          body: 'Neprivaloma. Įveskite poliso numerį ir paspauskite Enter („Enter policy numbers“); prireikus pridėkite kelis. Siūlomos tik tiksliai šių polisų įmokos.',
        },
        lv: {
          title: 'Ierobežot līdz konkrētiem polišu numuriem',
          body: 'Nav obligāti. Ierakstiet polises numuru un nospiediet Enter („Enter policy numbers“); ja vajag, pievienojiet vairākus. Tiek piedāvātas tikai tieši šo polišu iemaksas.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-subpolicies"]',
      title: 'Limit to specific policy sub-numbers',
      body: 'Optional. Type a policy sub-number and press Enter to add it. Only instalments with exactly these sub-numbers are suggested.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Apriboti iki konkrečių poliso sub numerių',
          body: 'Neprivaloma. Įveskite poliso sub numerį ir paspauskite Enter („Enter policy subnumbers“). Siūlomos tik tiksliai šių sub numerių įmokos.',
        },
        lv: {
          title: 'Ierobežot līdz konkrētiem polises apakšnumuriem',
          body: 'Nav obligāti. Ierakstiet polises apakšnumuru un nospiediet Enter („Enter policy subnumbers“). Tiek piedāvātas tikai tieši šo apakšnumuru iemaksas.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-broker"]',
      title: 'Choose broker or insurer premium collection',
      body: '"Yes" (the default) suggests only instalments whose premium GrECo collects and allows several insurers in one reminder. "No" suggests only instalments paid directly to the insurer and then requires exactly one insurer.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti, kas renka įmokas — brokeris ar draudikas',
          body: '„Yes“ (numatyta) siūlo tik įmokas, kurias renka GrECo, ir leidžia viename priminime kelis draudikus. „No“ siūlo tik tiesiogiai draudikui mokamas įmokas ir tada reikalauja lygiai vieno draudiko („Broker premium collection“).',
        },
        lv: {
          title: 'Izvēlēties, kas iekasē prēmiju — brokeris vai apdrošinātājs',
          body: '„Yes“ (noklusējums) piedāvā tikai iemaksas, kuru prēmiju iekasē GrECo, un ļauj vienā atgādinājumā iekļaut vairākus apdrošinātājus. „No“ piedāvā tikai tieši apdrošinātājam maksājamās iemaksas un tad prasa tieši vienu apdrošinātāju („Broker premium collection“).',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-include-existing"]',
      title: 'Include instalments already in a reminder',
      body: 'With "No" (the default) instalments that already belong to a reminder are left out, so a client is not reminded twice for the same instalment. Switch to "Yes" to send a follow-up reminder for them.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Įtraukti įmokas, jau esančias priminime',
          body: 'Pasirinkus „No“ (numatyta), įmokos, kurios jau yra kitame priminime, neįtraukiamos, kad klientui nebūtų priminta dukart dėl tos pačios įmokos („Include installments already in reminder“). Pasirinkite „Yes“, jei norite siųsti pakartotinį priminimą.',
        },
        lv: {
          title: 'Iekļaut iemaksas, kas jau ir atgādinājumā',
          body: 'Ar „No“ (noklusējums) iemaksas, kas jau ir citā atgādinājumā, netiek iekļautas, lai klientam par to pašu iemaksu neatgādinātu divreiz („Include installments already in reminder“). Izvēlieties „Yes“, lai sūtītu atkārtotu atgādinājumu.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-search"]',
      title: 'Find the instalments to remind',
      body: 'Asks the server for the client’s unpaid instalments (for a reminder by payer: those of the selected clients) that match the filters and fills the list on the right, all rows selected, each with its outstanding debt as the reminder value. It also proposes a reminder number, except for GrECo Latvia, where the field stays empty and the number is generated when the reminder is saved. The button stays disabled while no insurer is picked and "Broker premium collection" is "No".',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Rasti įmokas priminimui',
          body: 'Paprašo serverio kliento nesumokėtų įmokų (mokėtojo priminimui — pasirinktų klientų įmokų) pagal filtrus ir užpildo sąrašą dešinėje: visos eilutės pažymėtos, kiekvienos priminimo suma lygi likusiai skolai. Kartu pasiūlomas priminimo numeris, išskyrus GrECo Latvia – ten laukas lieka tuščias, o numeris sugeneruojamas išsaugant priminimą. Mygtukas „Ieškoti“ neaktyvus, kol nepasirinktas draudikas ir „Broker premium collection“ yra „No“.',
        },
        lv: {
          title: 'Atrast iemaksas atgādinājumam',
          body: 'Pieprasa no servera klienta neapmaksātās iemaksas (maksātāja atgādinājumam — izvēlēto klientu iemaksas) atbilstoši filtriem un aizpilda sarakstu labajā pusē: visas rindas atzīmētas, katrai atgādinājuma summa ir atlikušais parāds. Tiek piedāvāts arī atgādinājuma numurs, izņemot GrECo Latvia – tur lauks paliek tukšs, un numurs tiek ģenerēts, saglabājot atgādinājumu. Poga „Search“ nav aktīva, kamēr nav izvēlēts apdrošinātājs un „Broker premium collection“ ir „No“.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-payer"]',
      title: 'Set who receives the reminder',
      body: 'The payer starts as the selected client; change it when another partner pays on the client’s behalf, or tick "Enter payer name manually" below to type the name of a private person who has no partner record. For a reminder by payer it is the payer selected on the page and cannot be changed. The reminder documents are addressed to this payer, and "Create reminder" stays disabled while the field is empty.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Nustatyti, kam skirtas priminimas',
          body: 'Mokėtojas („Mokėtojas“) iš pradžių yra pasirinktas klientas; pakeiskite, jei už klientą moka kitas partneris, arba pažymėkite žemiau „Įvesti mokėtojo vardą rankiniu būdu“ ir įrašykite fizinio asmens, kuris neturi partnerio įrašo, vardą ir pavardę. Mokėtojo priminimui tai puslapyje pasirinktas mokėtojas, jo pakeisti negalima. Priminimo dokumentai adresuojami šiam mokėtojui, o „Create reminder“ neaktyvus, kol laukas tuščias.',
        },
        lv: {
          title: 'Norādīt, kam atgādinājums adresēts',
          body: 'Maksātājs („Payer“) sākumā ir izvēlētais klients; nomainiet to, ja klienta vietā maksā cits partneris, vai atzīmējiet zemāk „Ievadīt maksātāja vārdu manuāli“ un ierakstiet privātpersonas, kurai nav partnera ieraksta, vārdu un uzvārdu. Maksātāja atgādinājumam tas ir lapā izvēlētais maksātājs, un to nevar mainīt. Atgādinājuma dokumenti tiek adresēti šim maksātājam, un „Create reminder“ nav aktīva, kamēr lauks ir tukšs.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-payer-manual"]',
      title: 'Address the reminder to a private person by name',
      body: 'Tick to type the payer’s name instead of picking a partner, for a private person who pays for the client but has no partner record. The field is pre-filled with the policy payer name of the selected instalments when they have one without a partner. The documents are then issued to a private person: the name is printed, with no registration number, legal address or VAT line (the address prints as "-" on Lithuanian documents). Such a reminder is listed under the client, shows the name without a number in the "Payer" column, and is not found when filtering by payer. Not shown for a reminder by payer.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Adresuoti priminimą fiziniam asmeniui pagal vardą',
          body: 'Pažymėkite, jei norite įrašyti mokėtojo vardą ir pavardę vietoj partnerio pasirinkimo – fiziniam asmeniui, kuris moka už klientą, bet neturi partnerio įrašo. Laukas užpildomas pasirinktų įmokų poliso mokėtojo vardu, jei jis nurodytas be partnerio. Dokumentai tada išrašomi fiziniam asmeniui: spausdinamas vardas, be registracijos numerio, juridinio adreso ir PVM eilutės (adresas lietuviškuose dokumentuose spausdinamas kaip „-“). Toks priminimas rodomas prie kliento, stulpelyje „Mokėtojas“ rodomas vardas be numerio, o filtruojant pagal mokėtoją jis nerandamas. Nerodoma mokėtojo priminimui.',
        },
        lv: {
          title: 'Adresēt atgādinājumu privātpersonai pēc vārda',
          body: 'Atzīmējiet, lai ierakstītu maksātāja vārdu, nevis izvēlētos partneri – privātpersonai, kura maksā par klientu, bet kurai nav partnera ieraksta. Lauks tiek aizpildīts ar izvēlēto iemaksu polises maksātāja vārdu, ja tas norādīts bez partnera. Dokumenti tad tiek izrakstīti privātpersonai: tiek drukāts vārds, bez reģistrācijas numura, juridiskās adreses un PVN rindas (adrese Lietuvas dokumentos tiek drukāta kā „-“). Šāds atgādinājums ir redzams pie klienta, kolonnā „Payer“ rāda vārdu bez numura, un to neatrod, filtrējot pēc maksātāja. Netiek rādīts maksātāja atgādinājumam.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-number"]',
      title: 'Check the reminder number',
      body: 'Filled in by the search from the client number, the insurer abbreviation (when one insurer is chosen) and today’s date, for example 123456-ABC-20260101; you may change it or clear it. For GrECo Latvia the search leaves it empty. Left empty, the number is generated when the reminder is saved as "P", today’s date and the reminder’s id, for example P20260101-1234. A number you enter must be unique and at most 50 characters — a number already used by another reminder is rejected when you create it.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti priminimo numerį',
          body: 'Paieška užpildo jį iš kliento numerio, draudiko santrumpos (kai pasirinktas vienas draudikas) ir šiandienos datos, pvz., 123456-ABC-20260101; numerį galite pakeisti arba ištrinti. GrECo Latvia atveju paieška jį palieka tuščią. Palikus tuščią, numeris sugeneruojamas išsaugant priminimą iš „P“, šiandienos datos ir priminimo id, pvz., P20260101-1234. Įvestas numeris turi būti unikalus ir ne ilgesnis nei 50 simbolių — kuriant priminimą, jau naudojamas numeris atmetamas („Priminimo numeris“).',
        },
        lv: {
          title: 'Pārbaudīt atgādinājuma numuru',
          body: 'Meklēšana to aizpilda no klienta numura, apdrošinātāja saīsinājuma (ja izvēlēts viens apdrošinātājs) un šodienas datuma, piem., 123456-ABC-20260101; numuru var mainīt vai dzēst. GrECo Latvia gadījumā meklēšana to atstāj tukšu. Ja atstāj tukšu, numurs tiek ģenerēts, saglabājot atgādinājumu, no „P“, šodienas datuma un atgādinājuma id, piem., P20260101-1234. Ievadītam numuram jābūt unikālam un ne garākam par 50 rakstzīmēm — veidojot atgādinājumu, jau izmantots numurs tiek noraidīts („Reminder number“).',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-english"]',
      title: 'Generate the documents in English',
      body: 'Tick to use the English document template. Only GrECo Latvia has English templates; for the Lithuanian companies the documents are always generated in Lithuanian.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Generuoti dokumentus anglų kalba',
          body: 'Pažymėkite „Use English template if possible“, jei norite angliško dokumento šablono. Anglų kalbos šablonus turi tik GrECo Latvia; Lietuvos įmonių dokumentai visada generuojami lietuvių kalba.',
        },
        lv: {
          title: 'Ģenerēt dokumentus angļu valodā',
          body: 'Atzīmējiet „Use English template if possible“, lai izmantotu angļu valodas dokumenta veidni. Angļu veidnes ir tikai GrECo Latvia; Lietuvas uzņēmumu dokumenti vienmēr tiek ģenerēti lietuviešu valodā.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-comment"]',
      title: 'Check the comment (due date)',
      body: 'Pre-filled with the earliest booking date of the selected instalments and updated when the selection changes. GrECo Latvia documents print it as the invoice due date, and the reminder email uses it as the payment due date, so overwrite it with the date the client should pay by. At most 512 characters.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti komentarą (mokėjimo terminą)',
          body: 'Iš anksto užpildomas anksčiausia pažymėtų įmokų data ir atnaujinamas pasikeitus pasirinkimui. GrECo Latvia dokumentuose jis spausdinamas kaip sąskaitos apmokėjimo terminas, o priminimo el. laiške naudojamas kaip mokėjimo terminas, todėl įrašykite datą, iki kurios klientas turi sumokėti („Komentaras“). Ne daugiau kaip 512 simbolių.',
        },
        lv: {
          title: 'Pārbaudīt komentāru (apmaksas termiņu)',
          body: 'Iepriekš aizpildīts ar agrāko atzīmēto iemaksu datumu un tiek atjaunināts, mainot izvēli. GrECo Latvia dokumentos tas tiek drukāts kā rēķina apmaksas termiņš, bet atgādinājuma e-pastā — kā maksājuma termiņš, tāpēc ierakstiet datumu, līdz kuram klientam jāsamaksā („Comment“). Ne vairāk kā 512 rakstzīmes.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-grid"]',
      title: 'Choose instalments and amounts',
      body: 'The suggested instalments with premium, discount, paid amount and "Debt". A row on a light-red background belongs to a cancelled policy — decide whether it should be reminded at all. Untick a row in the checkbox column on the right to leave it out. "Reminder value" is the amount to remind; type a different value, or use its lightning button to fill it and the cross to set it to 0.',
      section: 'Create a reminder',
      context: 'create-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Pasirinkti įmokas ir sumas',
          body: 'Siūlomos įmokos su įmoka, nuolaida, sumokėta suma ir „Skola“. Šviesiai raudoname fone rodoma eilutė priklauso nutrauktam polisui – nuspręskite, ar apie ją apskritai reikia priminti. Atžymėkite eilutę dešiniajame žymimųjų langelių stulpelyje, jei jos nereikia. „Reminder value“ — priminimo suma; įveskite kitą reikšmę arba spauskite žaibo mygtuką, kad ją užpildytumėte, ir kryželį, kad nustatytumėte 0.',
        },
        lv: {
          title: 'Izvēlēties iemaksas un summas',
          body: 'Piedāvātās iemaksas ar prēmiju, atlaidi, apmaksāto summu un „Debt“. Rinda gaiši sarkanā fonā pieder anulētai polisei – izlemiet, vai par to vispār jāatgādina. Noņemiet atzīmi rindai labās puses izvēles rūtiņu kolonnā, lai to neiekļautu. „Reminder value“ ir atgādināmā summa; ierakstiet citu vērtību vai izmantojiet zibens pogu, lai to aizpildītu, un krustiņu, lai iestatītu 0.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-filter"]',
      title: 'Highlight instalments in the list',
      body: 'Type a policy name or number, insurer, insurance line, date or amount to highlight the matching rows and see how many match. It only highlights — no row is hidden or unselected.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Paryškinti įmokas sąraše',
          body: 'Įveskite poliso pavadinimą ar numerį, draudiką, draudimo rūšį, datą ar sumą — atitinkančios eilutės paryškinamos ir parodomas jų skaičius. Tai tik paryškinimas: jokia eilutė nepaslepiama ir neatžymima.',
        },
        lv: {
          title: 'Izcelt iemaksas sarakstā',
          body: 'Ierakstiet polises nosaukumu vai numuru, apdrošinātāju, apdrošināšanas veidu, datumu vai summu — atbilstošās rindas tiek izceltas un parādīts to skaits. Tā ir tikai izcelšana: neviena rinda netiek paslēpta vai atzīmēta.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-total"]',
      title: 'Check the total being reminded',
      body: 'Shows the sum of "Reminder value" over the selected rows and how many rows are selected. Check it before creating the reminder.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti priminimo sumą',
          body: 'Rodo pažymėtų eilučių „Reminder value“ sumą („Selected amount“) ir pažymėtų eilučių skaičių. Patikrinkite prieš kurdami priminimą.',
        },
        lv: {
          title: 'Pārbaudīt atgādināmo kopsummu',
          body: 'Rāda atzīmēto rindu „Reminder value“ summu („Selected amount“) un atzīmēto rindu skaitu. Pārbaudiet pirms atgādinājuma izveides.',
        },
      },
    },
    {
      selector: '[data-help="reminders-create-confirm"]',
      title: 'Create the reminder',
      body: 'Saves the reminder with the selected instalments and generates its PDF documents, then reloads the list. It is enabled only with a payer and at least one selected row; the reminder number may be left empty. If document generation fails (for example missing insurer bank details) nothing is saved.',
      section: 'Create a reminder',
      context: 'create-dialog',
      i18n: {
        lt: {
          title: 'Sukurti priminimą',
          body: 'Išsaugo priminimą su pažymėtomis įmokomis, sugeneruoja jo PDF dokumentus ir atnaujina sąrašą („Create reminder“). Mygtukas aktyvus tik esant mokėtojui ir bent vienai pažymėtai eilutei; priminimo numerį galima palikti tuščią. Jei dokumento sugeneruoti nepavyksta (pvz., trūksta draudiko banko duomenų), niekas neišsaugoma.',
        },
        lv: {
          title: 'Izveidot atgādinājumu',
          body: 'Saglabā atgādinājumu ar atzīmētajām iemaksām, ģenerē tā PDF dokumentus un pārlādē sarakstu („Create reminder“). Poga ir aktīva tikai, ja ir maksātājs un vismaz viena atzīmēta rinda; atgādinājuma numuru var atstāt tukšu. Ja dokumentu ģenerēšana neizdodas (piem., trūkst apdrošinātāja bankas datu), nekas netiek saglabāts.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-already-sent"]',
      title: 'Notice a reminder that was already sent',
      body: 'When the reminder has been emailed before, a warning shows when and to whom. You can still send it again; the new recipients are added to the reminder’s sent list.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Pastebėti, kad priminimas jau išsiųstas',
          body: 'Jei priminimas jau buvo siųstas el. paštu, įspėjimas parodo, kada ir kam. Siųsti dar kartą vis tiek galima; nauji gavėjai pridedami prie priminimo išsiuntimo sąrašo.',
        },
        lv: {
          title: 'Pamanīt, ka atgādinājums jau nosūtīts',
          body: 'Ja atgādinājums jau ir nosūtīts pa e-pastu, brīdinājums parāda, kad un kam. To joprojām var nosūtīt vēlreiz; jaunie saņēmēji tiek pievienoti atgādinājuma nosūtīšanas sarakstam.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-to"]',
      title: 'Check the recipient',
      body: 'Pre-filled with the premium collection email address from the client’s valid mandate. If none is set, a warning asks you to type the address yourself. Separate several addresses with commas or semicolons.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti gavėją',
          body: 'Laukas „To“ iš anksto užpildomas įmokų surinkimo el. pašto adresu iš galiojančio kliento įgaliojimo (mandato). Jei adreso nėra, įspėjimas paprašys jį įvesti patiems. Kelis adresus atskirkite kableliais arba kabliataškiais.',
        },
        lv: {
          title: 'Pārbaudīt saņēmēju',
          body: 'Lauks „To“ ir iepriekš aizpildīts ar prēmiju iekasēšanas e-pasta adresi no klienta derīgā pilnvarojuma (mandāta). Ja adrese nav norādīta, brīdinājums aicina to ierakstīt pašiem. Vairākas adreses atdaliet ar komatiem vai semikoliem.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-cc"]',
      title: 'Add copy recipients',
      body: 'Optional addresses that receive a copy of the email, separated by commas or semicolons. Every address is checked for a valid format before sending.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Pridėti kopijos gavėjus',
          body: 'Neprivalomi adresai („CC“), kuriems siunčiama laiško kopija, atskirti kableliais arba kabliataškiais. Prieš siunčiant kiekvieno adreso formatas patikrinamas.',
        },
        lv: {
          title: 'Pievienot kopijas saņēmējus',
          body: 'Neobligātas adreses („CC“), kas saņem e-pasta kopiju, atdalītas ar komatiem vai semikoliem. Pirms nosūtīšanas katras adreses formāts tiek pārbaudīts.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-subject"]',
      title: 'Edit the subject',
      body: 'Filled from the company’s email template with the reminder number. You can change it, but it cannot be empty.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Redaguoti temą',
          body: 'Tema („Subject“) užpildoma pagal įmonės el. laiško šabloną su priminimo numeriu. Ją galima keisti, bet ji negali būti tuščia.',
        },
        lv: {
          title: 'Rediģēt tematu',
          body: 'Temats („Subject“) tiek aizpildīts pēc uzņēmuma e-pasta veidnes ar atgādinājuma numuru. To var mainīt, bet tas nedrīkst būt tukšs.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-attachments"]',
      title: 'Choose the documents to attach',
      body: 'Lists the reminder PDFs; tick the ones to send. The preferred one is pre-ticked: for GrECo Latvia the document without discount, for the Lithuanian companies the document with discount.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti pridedamus dokumentus',
          body: 'Skiltyje „Attachments“ išvardyti priminimo PDF; pažymėkite, kuriuos siųsti. Pageidaujamas dokumentas pažymėtas iš anksto: GrECo Latvia — be nuolaidos („Document without discount“), Lietuvos įmonėms — su nuolaida („Document with discount“).',
        },
        lv: {
          title: 'Izvēlēties pievienojamos dokumentus',
          body: 'Sadaļā „Attachments“ uzskaitīti atgādinājuma PDF; atzīmējiet, kurus sūtīt. Vēlamais dokuments ir atzīmēts iepriekš: GrECo Latvia — bez atlaides („Document without discount“), Lietuvas uzņēmumiem — ar atlaidi („Document with discount“).',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-add-files"]',
      title: 'Attach more files',
      body: 'Adds files from your computer to the email, for example a payment schedule. Added files are listed above the button and can be removed with their delete icon.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Pridėti daugiau failų',
          body: 'Prideda prie laiško failus iš jūsų kompiuterio („Add more files“), pvz., mokėjimų grafiką. Pridėti failai rodomi virš mygtuko ir gali būti pašalinti jų trynimo piktograma.',
        },
        lv: {
          title: 'Pievienot citus failus',
          body: 'Pievieno e-pastam failus no jūsu datora („Add more files“), piemēram, maksājumu grafiku. Pievienotie faili redzami virs pogas, un tos var noņemt ar dzēšanas ikonu.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-body"]',
      title: 'Edit the message text',
      body: 'The text comes from the company’s template with the client, reminder number, due date, amount and your name and phone filled in. Edit it freely; the message cannot be empty.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Redaguoti laiško tekstą',
          body: 'Tekstas („Message“) paimamas iš įmonės šablono ir užpildomas klientu, priminimo numeriu, mokėjimo terminu, suma bei jūsų vardu ir telefonu. Jį galima laisvai keisti, bet laiškas negali būti tuščias.',
        },
        lv: {
          title: 'Rediģēt vēstules tekstu',
          body: 'Teksts („Message“) tiek ņemts no uzņēmuma veidnes un aizpildīts ar klientu, atgādinājuma numuru, apmaksas termiņu, summu un jūsu vārdu un tālruni. To var brīvi mainīt, bet vēstule nedrīkst būt tukša.',
        },
      },
    },
    {
      selector: '[data-help="reminders-send-button"]',
      title: 'Send the email',
      body: 'Sends the email in your name from your own mailbox (test environments use a configured sender instead) and marks the reminder as sent with the date and recipients. Replies go to the company’s reply-to inbox where one is configured. If sending fails, the reminder is not marked as sent.',
      section: 'Send a reminder by email',
      context: 'send-dialog',
      i18n: {
        lt: {
          title: 'Išsiųsti laišką',
          body: 'Išsiunčia laišką jūsų vardu iš jūsų pašto dėžutės („Send“; testinėse aplinkose — iš sukonfigūruoto siuntėjo) ir pažymi priminimą kaip išsiųstą, įrašydamas datą ir gavėjus. Atsakymai keliauja į įmonės atsakymų pašto dėžutę, jei ji sukonfigūruota. Jei siuntimas nepavyksta, priminimas nepažymimas kaip išsiųstas.',
        },
        lv: {
          title: 'Nosūtīt e-pastu',
          body: 'Nosūta e-pastu jūsu vārdā no jūsu pastkastes („Send“; testa vidēs — no konfigurēta sūtītāja) un atzīmē atgādinājumu kā nosūtītu ar datumu un saņēmējiem. Atbildes nonāk uzņēmuma atbilžu pastkastē, ja tāda ir konfigurēta. Ja nosūtīšana neizdodas, atgādinājums netiek atzīmēts kā nosūtīts.',
        },
      },
    },
    {
      selector: '[data-help="reminders-delete-comment"]',
      title: 'Say why the reminder is deleted',
      body: 'Optional comment stored with the deletion, for example "wrong amount, recreated". It helps colleagues understand later why the reminder disappeared.',
      section: 'Delete a reminder',
      context: 'delete-dialog',
      i18n: {
        lt: {
          title: 'Nurodyti trynimo priežastį',
          body: 'Neprivalomas komentaras („Komentaras“), išsaugomas kartu su trynimu, pvz., „neteisinga suma, sukurta iš naujo“. Jis padeda kolegoms vėliau suprasti, kodėl priminimas dingo.',
        },
        lv: {
          title: 'Norādīt dzēšanas iemeslu',
          body: 'Neobligāts komentārs („Comment“), kas tiek saglabāts kopā ar dzēšanu, piemēram, „nepareiza summa, izveidots no jauna“. Tas palīdz kolēģiem vēlāk saprast, kāpēc atgādinājums pazuda.',
        },
      },
    },
    {
      selector: '[data-help="reminders-delete-confirm"]',
      title: 'Confirm the deletion',
      body: 'Deletes the reminder together with all its instalment entries and reloads the list. The record is only marked as deleted, not erased. If someone changed the reminder in the meantime the server refuses — reload and try again.',
      section: 'Delete a reminder',
      context: 'delete-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti trynimą',
          body: 'Ištrina priminimą kartu su visomis jo įmokų eilutėmis ir atnaujina sąrašą („Ištrinti“). Įrašas tik pažymimas kaip ištrintas, bet nesunaikinamas. Jei kas nors tuo metu pakeitė priminimą, serveris atsisakys — atnaujinkite sąrašą ir bandykite dar kartą.',
        },
        lv: {
          title: 'Apstiprināt dzēšanu',
          body: 'Dzēš atgādinājumu kopā ar visām tā iemaksu rindām un pārlādē sarakstu („Delete“). Ieraksts tiek tikai atzīmēts kā dzēsts, nevis iznīcināts. Ja kāds pa to laiku atgādinājumu ir mainījis, serveris atteiks — pārlādējiet sarakstu un mēģiniet vēlreiz.',
        },
      },
    },
  ],
  i18n: {
    lt: {
      title: 'Priminimai',
      intro:
        'Priminimai klientams apie nesumokėtas įmokas pagal mokėjimų planus. Čia rasite esamus priminimus, sukursite naują iš kliento neapmokėtų įmokų (arba vieną mokėtojui adresuotą — iš kelių klientų įmokų), atsisiųsite jo PDF dokumentus ir išsiųsite klientui el. paštu; vėliau per banko išrašus gauti mokėjimai atsispindi apmokėtose sumose ir būsenos piktogramose.',
      sections: {
        'Getting around': 'Orientavimasis',
        'Finding a reminder': 'Priminimo paieška',
        'Reading the list': 'Sąrašo skaitymas',
        'Documents and row actions': 'Dokumentai ir eilutės veiksmai',
        'Create a reminder': 'Priminimo kūrimas',
        'Send a reminder by email': 'Priminimo siuntimas el. paštu',
        'Delete a reminder': 'Priminimo trynimas',
      },
      contexts: {
        'create-dialog': 'Lauke „Klientas“ pasirinkite klientą (arba lauke „Mokėtojas“ — mokėtoją), tada spauskite šalia esantį „Create reminder“.',
        'send-dialog': 'Priminimo eilutėje spauskite voko piktogramą („Send by email“); ji rodoma tik tada, kai priminimas turi dokumentą.',
        'delete-dialog': 'Priminimo eilutėje spauskite trynimo piktogramą („Delete reminder“).',
      },
      tips: [
        'Priminimai sudaromi iš nesumokėtų mokėjimų plano įmokų. Patys pinigai sudengiami puslapyje „Banko išrašai“; kai įmoka sumokama, jos priminimo apmokėtos sumos ir būsenos piktogramos čia pasikeičia.',
        'Būsenos piktogramos: žalia varnelė — sumokėta, pusė apskritimo — sumokėta iš dalies, tuščias apskritimas — nesumokėta, klaustukas — įmokos nerenka brokeris. Viršutinė piktograma skirta įmokai, apatinė — nuolaidai.',
        'Sukurto priminimo šiame puslapyje redaguoti negalima — jo dokumentai sugeneruojami vieną kartą, kuriant. Norėdami pataisyti, ištrinkite priminimą ir sukurkite naują.',
        'Ne gamybinėje aplinkoje kiekviename sugeneruotame PDF yra vandenženklis „THIS IS A TEST DOCUMENT“.',
        'Ribotos PCT rolės naudotojai gali peržiūrėti priminimus, tačiau serveris neleidžia jų kurti, siųsti ir trinti.',
      ],
    },
    lv: {
      title: 'Atgādinājumi',
      intro:
        'Atgādinājumi klientiem par neapmaksātām prēmijas iemaksām no maksājumu plāniem. Šeit atradīsiet esošos atgādinājumus, izveidosiet jaunu no klienta neapmaksātajām iemaksām (vai vienu maksātājam adresētu — no vairāku klientu iemaksām), lejupielādēsiet tā PDF dokumentus un nosūtīsiet to klientam pa e-pastu; vēlāk ar bankas izrakstiem saņemtie maksājumi parādās apmaksātajās summās un statusa ikonās.',
      sections: {
        'Getting around': 'Orientēšanās',
        'Finding a reminder': 'Atgādinājuma meklēšana',
        'Reading the list': 'Saraksta lasīšana',
        'Documents and row actions': 'Dokumenti un rindas darbības',
        'Create a reminder': 'Atgādinājuma izveide',
        'Send a reminder by email': 'Atgādinājuma nosūtīšana pa e-pastu',
        'Delete a reminder': 'Atgādinājuma dzēšana',
      },
      contexts: {
        'create-dialog': 'Laukā „Client“ izvēlieties klientu (vai laukā „Payer“ — maksātāju), tad noklikšķiniet blakus esošo „Create reminder“.',
        'send-dialog': 'Atgādinājuma rindā noklikšķiniet uz aploksnes ikonas („Send by email“); tā redzama tikai tad, ja atgādinājumam ir dokuments.',
        'delete-dialog': 'Atgādinājuma rindā noklikšķiniet uz dzēšanas ikonas („Delete reminder“).',
      },
      tips: [
        'Atgādinājumi tiek veidoti no neapmaksātām maksājumu plāna iemaksām. Pašu naudu sasaista lapā „Bankas izraksti“; kad iemaksa ir apmaksāta, tās atgādinājuma apmaksātās summas un statusa ikonas šeit mainās.',
        'Statusa ikonas: zaļa ķeksīte — apmaksāts, pusaplis — daļēji apmaksāts, tukšs aplis — nav apmaksāts, jautājuma zīme — prēmiju neiekasē brokeris. Augšējā ikona attiecas uz prēmiju, apakšējā — uz atlaidi.',
        'Izveidotu atgādinājumu šajā lapā rediģēt nevar — tā dokumenti tiek ģenerēti vienreiz, izveides brīdī. Lai labotu, dzēsiet atgādinājumu un izveidojiet jaunu.',
        'Ārpus ražošanas vides katram ģenerētajam PDF ir ūdenszīme „THIS IS A TEST DOCUMENT“.',
        'Lietotāji ar ierobežoto PCT lomu var skatīt atgādinājumus, taču serveris neļauj tos izveidot, nosūtīt un dzēst.',
      ],
    },
  },
};
